import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LoggerService } from 'src/app/core/services/http/logger.service';
import liveSession from '@livesession/sdk';
import { OrganizationService } from 'src/app/core/services/http/agency-mgmt/organization.service';
import { UserSessionService } from 'bna-angular-auth-client';
import { ClientSettingsService } from 'src/app/core/services/auth/client-settings.service';
import { OrganizationStartupSettingsResult } from 'src/app/shared/models/agency-mgmt/manager.service.model';
import { AppSettings } from 'src/app/shared/constants/app-settings.constants';
import { SessionStorageService } from 'src/app/core/services/data/session-storage.service';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent implements OnInit, OnDestroy {
  private signing = false;
  private returnUrl = '';
  private subscriptions: Subscription[] = [];
  public errorMessage: string;

  constructor(
    private logger: LoggerService,
    public userSessionService: UserSessionService,
    private organizationService: OrganizationService,
    private clientSettingsService: ClientSettingsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sessionStorageService: SessionStorageService
  ) { }

  async ngOnInit() {
    let integrationType = this.activatedRoute.snapshot?.queryParams['integration-type'];
    localStorage.setItem("integration-type", integrationType);

    this.signing = await this.userSessionService.signing();
    this.returnUrl = this.getReturnUrl();

    this.redirectToSignInPageIfNotSignedIn();
    this.redirectToDefaultPageAfterSignedIn();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private getReturnUrl() {
    return this.activatedRoute.snapshot?.queryParams['returnUrl']
      ? this.activatedRoute.snapshot?.queryParams['returnUrl'][0]
      : null;
  }

  private redirectToDefaultPageAfterSignedIn() {
    this.userSessionService.whenSignedInCompleted().then(() => {
      this.signing = false;
      this.logger.log('redirectToDefaultPageAfterSignedIn');
      this.identifyAuthenticatedUserForLiveSession();
      this.appEnableValidityCheckPassed().then((passed) => {
        if (passed) {
          this.redirectToDefaultPage();
          return;
        }

        this.userSessionService.signOut(true);
      });
    });
  }

  private redirectToSignInPageIfNotSignedIn() {
    const yes = this.userSessionService.isIntegration;
    this.logger.log('iframe integration check: ' + yes);

    if (yes || this.signing) return;

    this.logger.log('redirectToSignInPageIfNotSignedIn');
    this.signIn();
  }

  private signIn() {
    this.rememberPropertySearch();
    const isAuthenticated = this.userSessionService.isAuthenticated();
    if (isAuthenticated) {
      this.redirectToDefaultPage();
      return;
    }
    this.signing = true;
    this.userSessionService.signIn();
  }

  private rememberPropertySearch() {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (
        params['external-property-id'] != undefined &&
        params['external-property-id']
      ) {
        this.sessionStorageService.set(
          'external-property-id',
          params['external-property-id']
        );
      } else {
        this.sessionStorageService.remove('external-property-id');
      }
    });
  }

  private redirectToDefaultPage() {
    if (this.returnUrl) {
      this.logger.log('redirecting to return url: ' + this.returnUrl);
      this.router.navigate([this.returnUrl]);
      return;
    } else {
      this.logger.log('redirecting to default page');
      this.router.navigate(['/job']);
      return;
    }
  }

  private identifyAuthenticatedUserForLiveSession() {
    const userClaims = this.userSessionService.getProfile();
    const properties = {
      name: userClaims.name,
      email: userClaims.email,
      params: {
        userType: userClaims.userGroup,
        countryCode: userClaims.country?.code,
        phone: userClaims.phoneNumber,
      },
    };
    this.logger.log('Live session identify API called', properties);
    liveSession.identify(properties);
  }

  private appEnableValidityCheckPassed() {
    return new Promise<boolean>((resolve) => {
      this.isAppEnabledForCurrentOrganization().then((passed) => {
        this.logger.log('isAppEnabledForCurrentOrganization:' + passed);
        if (passed) {
          return this.isCurrentOrganizationIsActive().then((passed) => {
            this.logger.log('isCurrentOrganizationIsActive:' + passed);
            resolve(passed);
          });
        }
        this.logger.log('appEnableValidityCheck:failed');
        return resolve(false);
      });
    });
  }

  private isAppEnabledForCurrentOrganization() {
    return new Promise<boolean>((resolve) => {
      const passed = this.configurationCheckPassed;
      this.logger.log('configurationCheckPassed:' + passed);
      if (passed) return resolve(true);
      this.logger.log(
        'userSessionService.isIntegration:' +
        this.userSessionService.isIntegration
      );
      if (this.userSessionService.isIntegration) return resolve(true);
      this.organizationService.getStartupSettings().subscribe((result) => {
        this.logger.log('getStartupSettings' + result);
          if (result != null && !result.newPmAppEnabled) {
            this.logger.log('new pm app is disabled for the organization');
            this.errorMessage =
              'This app is not enabled for your agency. If you want to enable it please contact our support team on <a href="mailto:support@bricksandagent.com" class="red-link">support@bricksandagent.com</a>.';
          }
          this.rememberAppEnabledOrganization(result);
          return resolve(result.newPmAppEnabled);
        
      });
    });
  }
  private isCurrentOrganizationIsActive() {
    return new Promise<boolean>((resolve) => {
      this.organizationService.getStartupSettings().subscribe((result) => {
        this.logger.log('getStartupSettings');
        this.logger.log(result);
        if (result != null && result?.isOrganizationNotActivated) {
          this.logger.log('OrganizationNotActivated');
          this.userSessionService.signOut(true);
          this.router.navigate(['/account-in-review']);
          resolve(false);
        }
        this.logger.log('OrganizationIsActivated');
        return resolve(true);
      });
    });
  }

  private rememberAppEnabledOrganization(
    result: OrganizationStartupSettingsResult
  ) {
    this.logger.log(
      'rememberAppEnabledOrganization, result.newPmAppEnabled' + result
    );
    this.logger.log(
      'organizationId:' + this.userSessionService.getProfile().organizationId
    );
    if (!result?.newPmAppEnabled) return;
    this.clientSettingsService.set(
      AppSettings.AppEnabledOrganizationId,
      this.userSessionService.getProfile().organizationId,
      false
    );

    this.clientSettingsService.set(
      AppSettings.IsStrataMasterEnabled,
      result?.isStrataMasterEnabled,
      false
    );

    this.clientSettingsService.set(
      AppSettings.IsCreditCardActivatedOrg,
      result?.isCreditCardActivatedOrg,
      false
    );
    this.clientSettingsService.set(
      AppSettings.IsAssetRegisterViewEnabled,
      result?.isAssetRegisterViewEnabled,
      false
    );
  }

  public signOut() {
    this.userSessionService.signOut();
  }

  public get configurationCheckPassed() {
    const enabledOrganizationId = this.clientSettingsService.get(
      AppSettings.AppEnabledOrganizationId,
      false
    );
    return (
      !!enabledOrganizationId &&
      enabledOrganizationId ==
      this.userSessionService.getProfile().organizationId
    );
  }
}
